<template>
  <b-card>
    <h5 class="mb-2"><b>Required Parameters</b></h5>
    <validation-observer ref="addReport">
      <b-form>
        <b-row class="justify-content-center">
          <b-col cols="12">
            <validation-provider name="Name" rules="required" v-slot="{ errors }">
              <b-form-group label="Name" label-cols-md="4">
                <b-form-input
                  autofocus
                  placeholder="Report Name"
                  v-model="newReport.name"
                  :state="errors[0] ? false : null"
                  class="form-control-merge"
                ></b-form-input>
                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="timeSelectionLabel" label-cols-md="4">
              <TimeRangeSelector
                v-if="isRangeReport"
                :minDate="minDate"
                :maxDate="maxDate"
                :initRange="timeRange"
                @change-time-range="setTimeRange"
                id="wide-time-range"
                class="mb-1"
              ></TimeRangeSelector>
              <TimeSelector
                v-else
                :minDate="maxDate"
                @change-time="setScheduleTime"
                id="wide-time-range"
                class="mb-1"
              ></TimeSelector>
              <b-row align-v="center">
                <b-col cols="8">
                  <b-form-radio-group
                    v-model="reportSelected"
                    class="d-flex justify-content-center align-content"
                    :options="reportOptions"
                    value-field="item"
                    text-field="name"
                    @change="setScheduleReport"
                  >
                  </b-form-radio-group>
                </b-col>
                <b-col v-if="!isRangeReport" cols="4">
                  <b-form-select
                    @change="setDuration"
                    v-model="recurrenceSelected"
                    :options="recurrenceOptions"
                  >
                  </b-form-select>
                </b-col>
            </b-row>
            </b-form-group>
          </b-col>
          <TopNTypeSelection
            v-if="newReport.type == reportTypes.topN || isXtendTopN"
            :selectedPrimaryEntity="selectedReportType.primaryEntityType"
            @change-topn-params="handleEmittedParams"
          ></TopNTypeSelection>
          <DevicesTypeSelection
            v-else-if="newReport.type == reportTypes.devices"
            @change-devices-params="handleEmittedParams"
          ></DevicesTypeSelection>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import moment from 'moment';

import DevicesTypeSelection from '@/xvisor/components/report/report-types/DevicesTypeSelection.vue';
import TimeSelector from '@/xvisor/components/TimeSelector.vue';
import TopNTypeSelection from '@/xvisor/components/report/report-types/TopNTypeSelection.vue';
import { reportTypes } from '@/xvisor/constants/reports';
import timeRangeMixin from '@/xvisor/components/timeRangeMixin';
import dateCeil from '@/xvisor/utilities/dateCeil';

const ReportEnum = Object.freeze({
  CREATE: 'create',
  SCEHDULE: 'schedule',
});

export default {
  mixins: [timeRangeMixin],
  components: {
    DevicesTypeSelection,
    TimeSelector,
    TopNTypeSelection,
  },
  props: {
    selectedReportType: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      newReport: this.initReportForm(),
      appInfras: [],
      reportTypes,
      reportSelected: ReportEnum.CREATE,
      reportOptions: [
        { item: ReportEnum.CREATE, name: 'Create Report' },
        { item: ReportEnum.SCEHDULE, name: 'Schedule Report' },
      ],
      recurrenceSelected: '24h',
      recurrenceOptions: [
        { value: '24h', text: 'Daily' },
        { value: '168h', text: 'Weekly' },
        { value: '730h', text: 'Monthly' },
      ],
    };
  },
  computed: {
    reportTypeOptions() {
      return Object.values(reportTypes);
    },
    isXtendTopN() {
      return this.newReport.type === 'Top Programs' || this.newReport.type === 'Top Urls';
    },
    isRangeReport() {
      return this.reportSelected === ReportEnum.CREATE;
    },
    timeSelectionLabel() {
      return this.reportSelected === ReportEnum.CREATE ? 'Time Range' : 'Select Time';
    },
  },
  watch: {
    selectedReportType(reportType) {
      this.newReport.type = reportType ? reportType.type : undefined;
      this.newReport.params = {
        ...this.newReport.params,
        primaryEntityType: reportType ? reportType.primaryEntityType : null,
      };
    },
  },
  methods: {
    handleEmittedParams(params) {
      // Overwrite the relevant params that were emitted.
      this.newReport.params = { ...this.newReport.params, ...params };
    },
    initReportForm() {
      const end = moment(this.now).startOf('day').subtract(5, 'minutes').toDate();
      const start = moment(this.now).subtract(1, 'day').startOf('day').toDate();
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const newReport = {
        name: '',
        scheduleReport: false,
        type: this.selectedReportType ? this.selectedReportType.type : undefined,
        duration: '24h',
        startTime: start,
        endTime: end,
        scheduleTime: dateCeil(new Date()),
        params: {
          timeZone: tz || 'UTC',
          primaryEntityType: this.selectedReportType ? this.selectedReportType.primaryEntityType : null,
          primaryEntityCount: null,
          secondaryEntityType: null,
          secondaryEntityCount: null,
          metricType: null,
        },
      };
      return newReport;
    },
    resetForm() {
      this.newReport = this.initReportForm();
      this.reportSelected = ReportEnum.CREATE;
      this.recurrenceSelected = '24h';
      this.$nextTick(() => {
        if (this.$refs.addReport) {
          this.$refs.addReport.reset();
        }
      });
    },
    setDuration(value) {
      this.newReport.duration = value;
    },
    setScheduleReport(value) {
      this.newReport.scheduleReport = value === ReportEnum.SCEHDULE;
    },
    setScheduleTime(value) {
      this.newReport.scheduleTime = value.time;
    },
    setTimeRange(value) {
      this.newReport.startTime = value.start;
      this.newReport.endTime = value.end;
    },
    addReport(ref) {
      this.$refs.addReport.validate().then((result) => {
        if (result) {
          this.$http
            .post('/reports/add', this.newReport)
            .then((response) => {
              const { id } = response.data;
              const isScheduled = this.reportSelected === ReportEnum.SCEHDULE;
              ref.$bvToast.toast('Successfully added report', { variant: 'success' });
              ref.hide();
              this.$emit('added', { id, schedule: isScheduled });
            })
            .catch(() => {
              ref.$bvToast.toast('Failed to add report', { variant: 'danger' });
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.horizontal-line {
  width: 100%;
  padding: 10px 0px;
}
#wide-time-range {
  width: 100%;
}
</style>
